import React from 'react';
import { Box, Typography, Button, TextField, Accordion, AccordionSummary, AccordionDetails, CircularProgress, Chip, Divider, Stack, Alert, AlertTitle } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { approvePipelineCheckpoint, submitPipelineAnswer } from '../../utilityFunctions/WellnessUtil';
import Colors from '../../components/ui/Colors';
// Dashboard checkpoints (pipelineKind=DASHBOARD) — additive; chat/report stages render exactly as before.
import { useTranslation } from 'react-i18next';
import { approveCheckpoint, submitCheckpointAnswer } from '../../utilityFunctions/dashboardApi';
import DashboardPlanReview from '../dashboards/nl/DashboardPlanReview';
import EditPlanDiff from '../dashboards/nl/EditPlanDiff';

const DASHBOARD_STAGES = ['DASHBOARD_PLAN', 'EDIT_PLAN'];

function DataIntentsSection({ dataIntents, unmetMetrics }) {
    if (!dataIntents || dataIntents.length === 0) return null;
    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrackChangesIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Data Intents</Typography>
                <Chip label={dataIntents.length} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
            </Box>
            {dataIntents.map((m, i) => (
                <Accordion key={i} sx={{ mb: 0.5, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid #e3eaf5', borderRadius: '8px !important' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={i + 1} size="small" color="primary" sx={{ height: 20, width: 20, fontSize: 10, '& .MuiChip-label': { px: 0 } }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.metric || `Intent ${i + 1}`}</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                        <Stack spacing={0.5} sx={{ pl: 1 }}>
                            {m.grain && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 56 }}>Grain</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.primary' }}>{m.grain}</Typography>
                                </Box>
                            )}
                            {m.filters && Object.keys(m.filters).length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 56 }}>Filters</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.primary' }}>
                                        {Object.entries(m.filters).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                                    </Typography>
                                </Box>
                            )}
                            {!m.grain && (!m.filters || Object.keys(m.filters).length === 0) && (
                                <Typography variant="caption" sx={{ color: 'text.disabled' }}>No additional details</Typography>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            ))}
            {unmetMetrics && unmetMetrics.length > 0 && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff8e1', borderRadius: 1, border: '1px solid #ffe082' }}>
                    <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600, display: 'block', mb: 0.5 }}>Unmet metrics</Typography>
                    {unmetMetrics.map((u, i) => (
                        <Typography key={i} variant="caption" sx={{ color: 'warning.dark', display: 'block', pl: 1 }}>
                            • {u.metric}{u.reason ? ` — ${u.reason}` : ''}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default function HitlReviewPanel({ checkpoint, jwtToken, onResolved }) {
    const [knowledge, setKnowledge] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const { t } = useTranslation();
    const isDashboard = DASHBOARD_STAGES.includes(checkpoint.currentStage);

    let stageOutput = null;
    try {
        stageOutput = JSON.parse(checkpoint.stageOutputJson || '{}');
        console.log(stageOutput);
    } catch (e) { stageOutput = {}; }

    let diagnostics = null;
    try {
        diagnostics = JSON.parse(checkpoint.diagnosticsJson || '{}');
    } catch (e) { diagnostics = {}; }

    const verdict = diagnostics?.verdict;
    const issues = diagnostics?.issues || [];
    // Dashboard edit clarification: EDIT_PLAN with EMPTY ops; the question is in diagnostics.issues.
    // The answer is the primary action (approving without one fails the turn), so Approve is hidden.
    const isClarification = checkpoint.currentStage === 'EDIT_PLAN'
        && !(Array.isArray(stageOutput?.ops) && stageOutput.ops.length > 0);

    const handleApprove = async () => {
        setLoading(true);
        const result = isDashboard
            ? (await approveCheckpoint(checkpoint.checkpointId, jwtToken)).ok
            : await approvePipelineCheckpoint(checkpoint.checkpointId, jwtToken);
        setLoading(false);
        if (!result) {
            const swal = (await import('sweetalert')).default;
            swal({ title: "Error", text: isDashboard ? t('Dashboards.hitl.failed') : "Failed to approve checkpoint. It may have already been processed.", icon: "error" });
            return;
        }
        onResolved(true);
    };

    const handleSubmitKnowledge = async () => {
        if (!knowledge.trim()) return;
        setLoading(true);
        const result = isDashboard
            ? (await submitCheckpointAnswer(checkpoint.checkpointId, knowledge.trim(), jwtToken)).ok
            : await submitPipelineAnswer(checkpoint.checkpointId, knowledge.trim(), jwtToken);
        setLoading(false);
        if (!result) {
            const swal = (await import('sweetalert')).default;
            swal({ title: "Error", text: isDashboard ? t('Dashboards.hitl.failed') : "Failed to submit knowledge. The checkpoint may have already been processed.", icon: "error" });
            return;
        }
        onResolved(false);
    };

    return (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', padding: '1.5rem', backgroundColor: '#fafafa', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {isDashboard ? (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {isClarification ? t('Dashboards.nlEdit.clarification')
                                : checkpoint.currentStage === 'EDIT_PLAN' ? t('Dashboards.hitl.editPlanTitle') : t('Dashboards.hitl.planTitle')}
                        </Typography>
                        {checkpoint.sectionTitle && <Chip label={checkpoint.sectionTitle} size="small" color="primary" />}
                    </>
                ) : (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Review: {checkpoint.sectionTitle || 'Section'}
                        </Typography>
                        <Chip
                            label={checkpoint.currentStage === 'DATA_INTENT' ? 'Data Intent' : 'SQL Generation'}
                            size="small"
                            color="primary"
                        />
                    </>
                )}
                {verdict === 'DEGRADED' && (
                    <Chip icon={<WarningAmberIcon />} label="Degraded" size="small" color="warning" />
                )}
                {verdict === 'FAILED' && (
                    <Chip icon={<ErrorOutlineIcon />} label="Failed" size="small" color="error" />
                )}
            </Box>
            {checkpoint.analysisFocus && checkpoint.currentStage !== 'EDIT_PLAN' && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {checkpoint.analysisFocus}
                </Typography>
            )}

            {isClarification && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>{t('Dashboards.nlEdit.clarification')}</AlertTitle>
                    {issues.map((q, i) => (
                        <Typography key={i} variant="body1" sx={{ fontWeight: 500 }}>{q}</Typography>
                    ))}
                    {stageOutput?.user_input && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>"{stageOutput.user_input}"</Typography>
                    )}
                </Alert>
            )}

            {!isClarification && issues.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: verdict === 'FAILED' ? '#fff3f3' : '#fff8e1', borderRadius: 1, border: `1px solid ${verdict === 'FAILED' ? '#ffcdd2' : '#ffe082'}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: verdict === 'FAILED' ? 'error.main' : 'warning.dark', display: 'block', mb: 0.5 }}>
                        {verdict === 'FAILED' ? 'Errors detected' : 'Issues detected'}
                    </Typography>
                    {issues.map((issue, i) => (
                        <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', pl: 1 }}>
                            • {issue}
                        </Typography>
                    ))}
                </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* DATA INTENT display */}
            {checkpoint.currentStage === 'DATA_INTENT' && (
                <DataIntentsSection dataIntents={stageOutput.data_intents} unmetMetrics={stageOutput.unmet_metrics} />
            )}

            {/* SQL GENERATION display */}
            {checkpoint.currentStage === 'SQL_GENERATION' && (
                <Box sx={{ mb: 2 }}>
                    {stageOutput.queries && stageOutput.queries.length > 0 && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Generated SQL</Typography>
                                <Chip label={stageOutput.queries.length} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                            </Box>
                            {stageOutput.queries.map((q, i) => (
                                <Accordion key={i} sx={{ mb: 0.5, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px !important' }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                        <Typography variant="body2">{q.metric || `Query ${i + 1}`}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, p: 1, overflowX: 'auto' }}>
                                            <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                                {q.sql}
                                            </Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </>
                    )}
                    {stageOutput.blocked_metrics && stageOutput.blocked_metrics.length > 0 && (
                        <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff8e1', borderRadius: 1, border: '1px solid #ffe082' }}>
                            <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600, display: 'block', mb: 0.5 }}>Blocked metrics</Typography>
                            {stageOutput.blocked_metrics.map((b, i) => (
                                <Typography key={i} variant="caption" sx={{ color: 'warning.dark', display: 'block', pl: 1 }}>
                                    • {b.metric} — {b.reason}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* DASHBOARD PLAN display (DashboardPlanDto) */}
            {checkpoint.currentStage === 'DASHBOARD_PLAN' && <DashboardPlanReview plan={stageOutput} />}

            {/* EDIT PLAN display (EditPlanPreviewDto: ops + binding diff) */}
            {checkpoint.currentStage === 'EDIT_PLAN' && !isClarification && <EditPlanDiff preview={stageOutput} />}

            <Divider sx={{ mb: 2 }} />

            <TextField
                value={knowledge}
                onChange={e => setKnowledge(e.target.value)}
                placeholder={isDashboard ? t('Dashboards.hitl.answerLabel') : "Add knowledge or context (optional)..."}
                autoFocus={isClarification}
                multiline
                minRows={2}
                maxRows={6}
                fullWidth
                disabled={loading}
                sx={{ mb: 2 }}
            />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    {!isClarification && (
                    <Button
                        variant="outlined"
                        startIcon={<CheckCircleIcon />}
                        onClick={handleApprove}
                        sx={{ borderRadius: '16px', textTransform: 'none' }}
                    >
                        {isDashboard
                            ? (checkpoint.currentStage === 'EDIT_PLAN' ? t('Dashboards.hitl.approveEdit') : t('Dashboards.hitl.approve'))
                            : 'Approve'}
                    </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={handleSubmitKnowledge}
                        disabled={!knowledge.trim()}
                        sx={{
                            backgroundColor: Colors.primary,
                            color: 'white',
                            borderRadius: '16px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: Colors.primary, opacity: 0.9 },
                        }}
                    >
                        {isDashboard ? (isClarification ? t('Dashboards.common.send') : t('Dashboards.hitl.answerSubmit')) : 'Submit Knowledge'}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
